import React, { FC, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import InputSearch, { InputSize } from '@zvuk-b2b/react-uikit/ui/InputSearch';

import useDropdownPlacement from 'application/hooks/useDropdownPlacement';
import ReportRecipientOption from './ReportRecipientOption';
import ReportSelectionHeader from '../ReportSelectionHeader';
import ReportSelectTrigger from '../ReportSelectTrigger';
import { TEXTS } from '../texts';
import {
  DATA_TEST_IDS,
  REPORT_RECIPIENTS_DROPDOWN_MAX_HEIGHT,
  REPORT_RECIPIENTS_DROPDOWN_MIN_HEIGHT,
  REPORT_RECIPIENTS_DROPDOWN_PREFERRED_HEIGHT,
  REPORT_RECIPIENTS_DROPDOWN_VIEWPORT_GAP,
} from '../consts';
import { RECIPIENTS_TRIGGER_LABELLED_BY, REPORT_RECIPIENTS_DROPDOWN_GAP } from './consts';
import { ReportRecipientsSelectProps } from './types';

import styles from './index.module.scss';

const ReportRecipientsSelect: FC<ReportRecipientsSelectProps> = ({
  afterTrigger,
  recipients,
  selectedRecipientIds,
  onChange,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [search, setSearch] = useState('');

  const dropdownState = useDropdownPlacement({
    containerRef: triggerRef,
    isOpen: isDropdownOpen,
    gap: REPORT_RECIPIENTS_DROPDOWN_GAP,
    maxHeight: REPORT_RECIPIENTS_DROPDOWN_MAX_HEIGHT,
    minHeight: REPORT_RECIPIENTS_DROPDOWN_MIN_HEIGHT,
    preferredHeight: REPORT_RECIPIENTS_DROPDOWN_PREFERRED_HEIGHT,
    viewportGap: REPORT_RECIPIENTS_DROPDOWN_VIEWPORT_GAP,
  });

  const recipientIds = useMemo(() => recipients.map((recipient) => recipient.id), [recipients]);

  useEffect(() => {
    const handleDocumentPointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('pointerdown', handleDocumentPointerDown);

    return () => document.removeEventListener('pointerdown', handleDocumentPointerDown);
  }, []);

  const filteredRecipients = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    if (!normalizedSearch) {
      return recipients;
    }

    return recipients.filter((recipient) => recipient.email.toLowerCase().includes(normalizedSearch));
  }, [recipients, search]);

  const selectedCount = selectedRecipientIds.length;
  const totalCount = recipients.length;
  const dropdownId = DATA_TEST_IDS.RECIPIENTS_SELECT_DROPDOWN;
  const hasSelectedRecipients = selectedCount > 0;
  const hasUnselectedRecipients = selectedCount < totalCount;

  const onRecipientChange = useCallback(
    (recipientId: string, value: boolean) => {
      const nextRecipientIds = (() => {
        if (value) {
          return selectedRecipientIds.includes(recipientId)
            ? selectedRecipientIds
            : [...selectedRecipientIds, recipientId];
        }

        return selectedRecipientIds.filter((currentId) => currentId !== recipientId);
      })();

      onChange(nextRecipientIds);
    },
    [onChange, selectedRecipientIds]
  );

  const onSelectAll = useCallback(() => {
    onChange(recipientIds);
  }, [onChange, recipientIds]);

  const onReset = useCallback(() => {
    onChange([]);
  }, [onChange]);

  const onTriggerClick = useCallback(() => {
    setIsDropdownOpen((currentValue) => !currentValue);
  }, []);

  return (
    <div
      className={styles.container}
      data-testid={DATA_TEST_IDS.RECIPIENTS_SELECT}
      ref={containerRef}>
      <span
        id={DATA_TEST_IDS.RECIPIENTS_SELECT_LABEL}
        className={styles.label}
        data-testid={DATA_TEST_IDS.RECIPIENTS_SELECT_LABEL}>
        {TEXTS.REPORT_RECIPIENTS_LABEL}
      </span>

      <ReportSelectTrigger
        ref={triggerRef}
        ariaLabelledBy={RECIPIENTS_TRIGGER_LABELLED_BY}
        count={TEXTS.REPORT_RECIPIENTS_SELECTED_COUNT_SHORT(selectedCount, totalCount)}
        countTestId={DATA_TEST_IDS.RECIPIENTS_SELECT_TRIGGER_COUNT}
        dropdownId={dropdownId}
        elementId={DATA_TEST_IDS.RECIPIENTS_SELECT_TRIGGER}
        iconTestId={DATA_TEST_IDS.RECIPIENTS_SELECT_TRIGGER_ICON}
        isOpen={isDropdownOpen}
        testId={DATA_TEST_IDS.RECIPIENTS_SELECT_TRIGGER}
        textTestId={DATA_TEST_IDS.RECIPIENTS_SELECT_TRIGGER_TEXT}
        title={TEXTS.REPORT_RECIPIENTS_SELECTED_TITLE}
        titleTestId={DATA_TEST_IDS.RECIPIENTS_SELECT_TRIGGER_TITLE}
        onClick={onTriggerClick}
      />

      {afterTrigger && <div className={styles.afterTrigger}>{afterTrigger}</div>}

      {isDropdownOpen && (
        <div
          id={dropdownId}
          className={styles.dropdown}
          style={{
            position: 'fixed',
            maxHeight: dropdownState.maxHeight,
            top: dropdownState.fixedPosition?.top,
            left: dropdownState.fixedPosition?.left,
            width: dropdownState.fixedPosition?.width,
          }}
          data-testid={DATA_TEST_IDS.RECIPIENTS_SELECT_DROPDOWN}>
          <InputSearch
            elementId={DATA_TEST_IDS.RECIPIENTS_SELECT_SEARCH}
            value={search}
            placeholder={TEXTS.REPORT_RECIPIENTS_SEARCH_PLACEHOLDER}
            size={InputSize.SMALL}
            containerClassName={styles.search}
            inputClassName={styles.searchInput}
            onChange={setSearch}
          />

          <div
            className={styles.divider}
            data-testid={DATA_TEST_IDS.RECIPIENTS_SELECT_DIVIDER}
          />

          <ReportSelectionHeader
            actionsDividerTestId={DATA_TEST_IDS.RECIPIENTS_SELECT_ACTIONS_DIVIDER}
            actionsTestId={DATA_TEST_IDS.RECIPIENTS_SELECT_ACTIONS}
            className={styles.selectionHeader}
            isMobileStacked
            resetAction={
              hasSelectedRecipients
                ? {
                    testId: DATA_TEST_IDS.RECIPIENTS_SELECT_RESET_BUTTON,
                    text: TEXTS.REPORT_RECIPIENTS_RESET,
                    onClick: onReset,
                  }
                : undefined
            }
            selectAllAction={
              hasUnselectedRecipients
                ? {
                    testId: DATA_TEST_IDS.RECIPIENTS_SELECT_SELECT_ALL_BUTTON,
                    text: TEXTS.REPORT_RECIPIENTS_SELECT_ALL,
                    onClick: onSelectAll,
                  }
                : undefined
            }
            selectedCount={TEXTS.REPORT_RECIPIENTS_SELECTED_COUNT(selectedCount)}
            selectedCountTestId={DATA_TEST_IDS.RECIPIENTS_SELECT_SELECTED_COUNT}
            testId={DATA_TEST_IDS.RECIPIENTS_SELECT_HEADER}
          />

          <ul
            className={styles.list}
            data-testid={DATA_TEST_IDS.RECIPIENTS_SELECT_LIST}>
            {filteredRecipients.map((recipient) => (
              <ReportRecipientOption
                key={recipient.id}
                recipient={recipient}
                isChecked={selectedRecipientIds.includes(recipient.id)}
                onChange={onRecipientChange}
              />
            ))}

            {filteredRecipients.length === 0 && (
              <li
                className={styles.empty}
                data-testid={DATA_TEST_IDS.RECIPIENTS_SELECT_EMPTY}>
                {TEXTS.REPORT_RECIPIENTS_EMPTY}
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default memo(ReportRecipientsSelect);
